'use client';
/**
 * mbar-live(2026-11-04 · Node F-4안) — 현장 상세 하단 바 「관심고객 등록」 버튼 위에 떠 있는 LIVE 배지.
 *   「● [LIVE] 이 현장 조회 2,431회」 — 흰 알약(24px) + 말풍선 꼬리, 버튼 위 22px 에 떠서 2초 주기로 2px 둥둥(reduced-motion 이면 정지).
 *
 *   · 서버 HTML 에 누적(apt_sites.page_views)을 바로 박는다 — 봇·방문자 무관 같은 HTML · 시프트 0.
 *   · 수화 뒤 /api/visitors/site?slug= 를 탭이 보일 때만 60초마다 — 올라간 값만 1초 굴림(내려가면 바로).
 *   · 0·null 이면 배지를 그리지 않는다(가짜 숫자 0). 글자·점·LIVE 칩은 aria-hidden — 스크린리더는 버튼의 aria-describedby 로 한 문장만 듣는다.
 *   · 기하·색은 live.css(.kd-sv) — 인라인 style 없음. 맥박 점·LIVE 칩은 기존 .kd-lv 한 벌.
 */
import React from 'react'; // vitest(클래식 JSX) 렌더 검증용
import { useEffect, useState } from 'react';
import { LiveBadge } from '@/components/live/LiveBadge';
import { formatCount } from '@/lib/nav/visitor-count';
import { parseSiteViewsResponse, siteViewsAria, SITE_VIEWS_API, SITE_VIEWS_POLL_MS } from '@/lib/live/site-views';
import './live.css';

export const SITE_VIEW_BADGE_ID = 'kd-sv-desc';

export function SiteViewBadgeView({ views }: { views: number | null }) {
  const aria = siteViewsAria(views);
  if (!aria || views == null) return null;
  return (
    <>
      <span className="kd-sv" data-site-views={views} aria-hidden="true">
        <span className="kd-lv__dot" />
        <LiveBadge className="kd-sv__lv" />
        <span className="kd-sv__t">이 현장 조회 <b className="kd-sv__n">{formatCount(views)}</b>회</span>
      </span>
      <span id={SITE_VIEW_BADGE_ID} className="kd-sr-only">{aria}</span>
    </>
  );
}

export default function SiteViewBadge({ slug, initial }: { slug: string; initial: number | null }) {
  const [views, setViews] = useState<number | null>(initial);
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    let shown = initial;
    const run = async () => {
      if (document.hidden) return;
      try {
        const r = await fetch(`${SITE_VIEWS_API}?slug=${encodeURIComponent(slug)}`);
        const next = parseSiteViewsResponse(r.ok ? await r.json() : null);
        if (!alive || next == null) return;
        const from = shown;
        shown = next;
        const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (typeof from !== 'number' || next <= from || reduce || typeof requestAnimationFrame !== 'function') { setViews(next); return; }
        const t0 = performance.now();
        const step = (t: number) => {
          if (!alive) return;
          const k = Math.min(1, (t - t0) / 1000);
          setViews(Math.round(from + (next - from) * k));
          if (k < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      } catch { /* 실패 — 서버 값 그대로, 다음 주기에 다시 */ }
    };
    const t = window.setInterval(run, SITE_VIEWS_POLL_MS);
    document.addEventListener('visibilitychange', run);
    return () => { alive = false; window.clearInterval(t); document.removeEventListener('visibilitychange', run); };
  }, [slug, initial]);
  return <SiteViewBadgeView views={views} />;
}
